import React, { useCallback } from 'react';
import { Identity } from './types';
import { useTranslation } from 'react-i18next';
import { useIdentity } from './hooks/useIdentity';
import useLocalStorage from '@rehooks/local-storage';

export const IsIshBarHiddenLocalStorageKey = 'IS_ISH_BAR_HIDDEN';

export const useIshBarMessage = (
  pathName: string,
  search: string,
  enabled = true,
) => {
  const { t } = useTranslation();
  const { data: identity } = useIdentity({ enabled });
  const [isIshBarHidden, setIsIshBarHidden] = useLocalStorage(
    IsIshBarHiddenLocalStorageKey,
  );

  const hideIshBar = useCallback(
    () => setIsIshBarHidden('true'),
    [setIsIshBarHidden],
  );

  if (isIshBarHidden) {
    return {
      message: null,
      hideIshBar,
    };
  }

  const IshOnlyBarMessage = {
    legacyARAndNewISPages: (
      <div
        dangerouslySetInnerHTML={{
          __html: t('ISHEnablementMessage.ishMessage'),
        }}
      />
    ),
  };

  const isIshOnOnly = (identity?: Identity) => {
    if (
      !identity?.permissions?.includes('permission.ARSettlement') &&
      identity?.permissions?.includes(
        'permission.InvoiceSettlementHarmonization',
      )
    ) {
      return true;
    }
    return false;
  };

  const isOldPaymentPage =
    isIshOnOnly(identity) &&
    pathName === '/apps/NewPayment.do' &&
    (!search.includes('method=') ||
      search.includes('method=create') ||
      search.includes('method=list') ||
      search.includes('method=view'));

  const isOldARPaymentPage =
    isIshOnOnly(identity) &&
    pathName === '/apps/ARPayment.do' &&
    (!search.includes('method=') ||
      search.includes('method=create') ||
      search.includes('method=list') ||
      search.includes('method=view'));

  const isNewPaymentPage =
    isIshOnOnly(identity) &&
    pathName.includes('/platform/apps/com_zuora/payment');

  const isOldRefundPage =
    isIshOnOnly(identity) &&
    pathName === '/apps/NewRefund.do' &&
    (!search.includes('method=') ||
      search.includes('method=create') ||
      search.includes('method=list') ||
      search.includes('method=view'));

  const isOldARRefundPage =
    isIshOnOnly(identity) &&
    pathName === '/apps/ARRefund.do' &&
    (!search.includes('method=') ||
      search.includes('method=create') ||
      search.includes('method=list') ||
      search.includes('method=view'));

  const isNewRefundPage =
    isIshOnOnly(identity) &&
    pathName.includes('/platform/apps/com_zuora/refund');

  const isOldIAPage =
    isIshOnOnly(identity) &&
    pathName === '/apps/Adjustment.do' &&
    (!search.includes('method=') ||
      search.includes('method=createInvoiceAdjustment') ||
      search.includes('method=list') ||
      search.includes('method=view'));

  const isNewIAPage =
    isIshOnOnly(identity) &&
    pathName.includes('/platform/apps/com_zuora/invoiceadjustment');

  const isOldIIAPage =
    isIshOnOnly(identity) &&
    pathName === '/apps/InvoiceItemAdjustment.do' &&
    (!search.includes('method=') ||
      search.includes('method=createInvoiceItemAdjustment') ||
      search.includes('method=list') ||
      search.includes('method=view'));

  const isNewIIAPage =
    isIshOnOnly(identity) &&
    pathName.includes('/platform/apps/com_zuora/invoiceitemadjustment');

  const isOldCBAPage =
    isIshOnOnly(identity) && pathName === '/apps/CreditBalanceAdjustment.do';

  const isOldMemoPage =
    isIshOnOnly(identity) && pathName === '/apps/ARSettlement.do';
  !search.includes('method=') ||
    search.includes('method=create') ||
    search.includes('method=list') ||
    search.includes('method=view');

  const isNewMemoPage =
    isIshOnOnly(identity) &&
    pathName.includes('/platform/apps/com_zuora/creditdebitmemos');

  if (
    isOldPaymentPage ||
    isNewPaymentPage ||
    isOldRefundPage ||
    isNewRefundPage ||
    isOldIAPage ||
    isNewIAPage ||
    isOldIIAPage ||
    isNewIIAPage ||
    isOldCBAPage ||
    isOldMemoPage ||
    isNewMemoPage ||
    isOldARPaymentPage ||
    isOldARRefundPage
  ) {
    return {
      message: IshOnlyBarMessage.legacyARAndNewISPages,
      hideIshBar,
    };
  }

  return {
    message: null,
    hideIshBar,
  };
};
